import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const preferenceToBool = (value) => {
    if (value === true) return true
    if (value === false || value == null) return false
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        if (normalized === 'true' || normalized === 'yes') return true
        if (normalized === 'false' || normalized === 'no') return false
    }
    return false
}

const fetchPreferences = async ({appOrigin, proxyPath, organizationId, siteId, token, preferenceIds}) => {
    const cIds = preferenceIds.join(',')
    const url = `${appOrigin}${proxyPath}/custom/preferences/v1/organizations/${organizationId}/preferences?siteId=${siteId}&c_ids=${cIds}`

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        }
    })

    if (!response.ok) {
        throw new Error('Failed to fetch preferences')
    }

    const data = await response.json()
    return data
}

const useSitePreferences = (preferenceIds) => {
    const appOrigin = useAppOrigin()
    const {getTokenWhenReady} = useAccessToken()
    const {site} = useMultiSite()
    const config = getConfig()
    const {proxyPath, parameters} = config.app.commerceAPI
    const organizationId = parameters.organizationId
    const siteId = site?.id || parameters.siteId
    const onClient = typeof window !== 'undefined'
    const ids = Array.isArray(preferenceIds) ? preferenceIds : [preferenceIds]
    const key = ids.slice().sort().join(',')

    const {data, isLoading, error} = useQuery({
        queryKey: ['sitePreferences', organizationId, siteId, key],
        enabled: onClient && !!organizationId && !!siteId && ids.length > 0,
        queryFn: async () => {
            const token = await getTokenWhenReady()
            return fetchPreferences({
                appOrigin,
                proxyPath,
                organizationId,
                siteId,
                token,
                preferenceIds: ids
            })
        },
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false
    })

    return {
        data: data || {},
        isLoading: onClient ? isLoading : false,
        error
    }
}

export {useSitePreferences, preferenceToBool}
